import React, { useEffect, useState } from "react";
import { Row, Col, Container, Stack, Button, Spinner } from "react-bootstrap";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import {
  banGuardian,
  banMember,
  fetchCase,
  fetchCaseStatuses,
  fetchCMUsers,
  fetchUserCaseGuardians,
  fetchUserCaseMembers,
  patchCase,
  patchGuardian,
  patchMember,
  postNote,
} from "../../services/httpClient";
import PageTitle from "../ui/PageTitle";
import SelectObject from "../ui/SelectObject";
import ToastMsg from "../ui/ToastMsg";
import Input from "../ui/Input";
import SaveChangesModal from "../ui/SaveChangesModal";
import { unsavedChanges, currentUrl } from "../../App";
import useApp from "../../utils/useApp";
import states from "../../data/states.json";
import { User } from "../../types/user";
import HouseholdCard from "./HouseholdCard";
import CreateMember from "./CreateMember";
import ExitNote from "./ExitNote";
import IntakeNote from "./IntakeNote";
import EditMember from "./EditMember";
import { Member } from "../../types/member";

interface State {
  key: string;
  name: string;
}

interface EditCaseState {
  limitedAccess: boolean;
}

const EditCase = ({ limitedAccess }: EditCaseState) => {
  const { showMenu, setActivePage }: any = useApp();
  const location = useLocation();
  currentUrl.value = location.pathname;
  const { id } = useParams();
  const [name, setName] = useState<string>("");
  const [primaryCM, setPrimaryCM] = useState<string>("");
  const [secondaryCM, setSecondaryCM] = useState<string>("");
  const [initialStatus, setInitialStatus] = useState<string>("");
  const [status, setStatus] = useState<string>("inactive");
  const [addressOne, setAddressOne] = useState<string>("");
  const [addressTwo, setAddressTwo] = useState<string>("");
  const [city, setCity] = useState<string>("");
  const [state, setState] = useState<string>("");
  const [zip, setZip] = useState<string>("");
  const [isBanned, setIsBanned] = useState<boolean>(false);
  const [duration, setDuration] = useState<string>("indefinite");
  const statesList: State[] = states;
  const [cmUsers, setCmUsers] = useState<any[]>([]);
  const [statuses, setStatuses] = useState<any[]>([]);
  const [guardians, setGuardians] = useState<User[]>([]);
  const [children, setChildren] = useState<User[]>([]);
  const [household, setHousehold] = useState<User[]>([]);
  const [selectedUser, setSelectedUser] = useState<User | Member | undefined>();
  const [userType, setUserType] = useState<string>("");
  const [showIntake, setShowIntake] = useState<boolean>(false);
  const [intakeDate, setIntakeDate] = useState<string>(new Date().toISOString().split("T")[0]);
  const [intakeNote, setIntakeNote] = useState<string>("");
  const [showExit, setShowExit] = useState<boolean>(false);
  const [exitDate, setExitDate] = useState<string>(new Date().toISOString().split("T")[0]);
  const [exitNote, setExitNote] = useState<string>();
  const [exitType, setExitType] = useState<string>("voluntary");
  const [showModal, setShowModal] = useState<boolean>(false);
  const [showCreateFamily, setShowCreateFamily] = useState<boolean>(false);
  const [showEditFamily, setShowEditFamily] = useState<boolean>(false);
  const [saveIsEnabled, setSaveIsEnabled] = useState<boolean>(false);
  const [errMsg, setErrMsg] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [showToast, setShowToast] = useState<boolean>(false);
  const [toastBody, setToastBody] = useState<string>("");
  const [toastText, setToastText] = useState<string>("");
  const [leavePath, setLeavePath] = useState<string>("/cases");
  const [userToSave, setUserToSave] = useState<boolean>(false);

  let navigate = useNavigate();

  useEffect(() => {
    if (userToSave) {
      unsavedChanges.value = true;
    } else {
      unsavedChanges.value = false;
    }
  }, [userToSave]);

  const backCase = () => {
    if (saveIsEnabled) {
      setLeavePath("/cases");
      setShowModal(true);
    } else {
      navigate(`/cases`);
      setShowCreateFamily(false);
    }
  };

  useEffect(() => {
    getCase();
    getHousehold();
  }, []);

  const getCase = () => {
    setIsLoading(true);
    getCaseStatuses();

    fetchCase(id)
      .then((res: any) => {
        const currentCase = res[0].data;
        setName(currentCase.name);
        setPrimaryCM(currentCase.primaryCM?.user_id);
        setSecondaryCM(currentCase.secondaryCM?.user_id);
        setStatus(currentCase.casestatus);
        setAddressOne(currentCase.address_line_one);
        setAddressTwo(currentCase.address_line_two);
        setCity(currentCase.city);
        setState(currentCase.state);
        setZip(currentCase.zip);

        setInitialStatus(currentCase.casestatus);
        //reset intake and exit
        setShowIntake(false);
        setShowExit(false);
        setIntakeNote("");
        setIntakeDate(new Date().toISOString().split("T")[0]);
        setExitNote("");
        setExitDate(new Date().toISOString().split("T")[0]);
        setExitType("voluntary");
        setIsBanned(false);
        setDuration("indefinite");

        fetchCMUsers()
          .then((res: any) => {
            const users = res[0].data.map((i: any) => {
              return {
                id: i.id,
                key: i.id,
                name: i.firstname.length === 0 ? i.email : `${i.firstname} ${i.lastname}`,
              };
            });
            setCmUsers(users);
          })
          .finally(() => {
            setIsLoading(false);
            if (location.state?.toastText) {
              setShowToast(true);
              setToastText(location.state?.toastText);
              setToastBody(location.state?.toastBody);
            }
          });
      })
      .catch((err: any) => {
        console.log(err);
        setIsLoading(false);
      });
  };

  const getHousehold = () => {
    fetchUserCaseGuardians(id)
      .then((res: any) => {
        const g = res[0].data;
        setGuardians(g);

        fetchUserCaseMembers(id)
          .then((res: any) => {
            const m = res[0].data;
            const c = m.filter((u: any) => u.relationship === "child");
            setChildren(c);
            const h = m.filter((u: any) => u.relationship !== "child");
            setHousehold(h);
          })
          .catch((err: any) => {
            console.log(err);
            setIsLoading(false);
          });
      })
      .catch((err: any) => {
        console.log(err);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  useEffect(() => {
    if (saveIsEnabled) {
      unsavedChanges.value = true;
    } else {
      unsavedChanges.value = false;
    }
  }, [saveIsEnabled]);

  const getCaseStatuses = async () => {
    await fetchCaseStatuses().then((res: any) => {
      setStatuses(res[0].data);
    });
  };

  const saveCase = async () => {
    setIsLoading(true);
    setErrMsg("");

    const updatedCase = {
      name: name,
      primaryCM: primaryCM,
      secondaryCM: secondaryCM,
      casestatus: status,
      address_line_one: addressOne,
      address_line_two: addressTwo,
      city: city,
      state: state,
      zip: zip,
    };

    patchCase(updatedCase, id)
      .then((res: any) => {
        setSaveIsEnabled(false);
        setShowToast(true);
        setToastText("Case Updated");
        setToastBody(name);
      })
      .catch((err: any) => {
        console.log(err);
        setErrMsg(err?.response?.data?.msg);
      });

    //this means the cases is being deactivated
    if (initialStatus === "active" && status !== "active") {
      if (isBanned) {
        exitOnStatusChange();
        await Promise.all([banOnStatusChange()]);
      } else {
        await Promise.all([exitOnStatusChange()]);
      }
    } else if (initialStatus !== "active" && status === "active") {
      //reactivating case
      await Promise.all([intakeOnStatusChange()]);
    }

    setTimeout(() => {
      getCase();
      getHousehold();
    }, 1500);
  };

  const exitOnStatusChange = () => {
    guardians.forEach((g: any) => {
      const note = {
        notetype: "exit",
        subject: {
          id: g?.id,
          type: "guardian",
        },
        fields: {
          exit_type: exitType,
          description: exitNote ?? "",
          date: new Date().toISOString().split("T")[0],
        },
      };

      //save exit note
      postNote(note, id)
        .then((res: any) => {
          const noteId = res[0].data.note_id;

          //update user exit data
          const exitData = {
            exit_at: exitDate,
            exit_note_id: noteId,
          };

          patchGuardian(exitData, id, g?.id).catch((err: any) => {
            console.log(err);
          });
        })
        .catch((err: any) => {
          console.log(err);
        });
    });

    household.forEach((h: any) => {
      const note = {
        notetype: "exit",
        subject: {
          id: h?.id,
          type: "member",
        },
        fields: {
          exit_type: exitType,
          description: exitNote ?? "",
          date: new Date().toISOString().split("T")[0],
        },
      };

      //save exit note
      postNote(note, id)
        .then((res: any) => {
          const noteId = res[0].data.note_id;

          //update user exit data
          const exitData = {
            exit_at: exitDate,
            exit_note_id: noteId,
          };

          patchMember(exitData, id, h?.id).catch((err: any) => {
            console.log(err);
          });
        })
        .catch((err: any) => {
          console.log(err);
        });
    });

    children.forEach((c: any) => {
      const note = {
        notetype: "exit",
        subject: {
          id: c?.id,
          type: "member",
        },
        fields: {
          exit_type: exitType,
          description: exitNote ?? "",
          date: new Date().toISOString().split("T")[0],
        },
      };

      //save exit note
      postNote(note, id)
        .then((res: any) => {
          const noteId = res[0].data.note_id;

          //update user exit data
          const exitData = {
            exit_at: exitDate,
            exit_note_id: noteId,
          };

          patchMember(exitData, id, c?.id).catch((err: any) => {
            console.log(err);
          });
        })
        .catch((err: any) => {
          console.log(err);
        });
    });

    setShowExit(false);
  };

  const intakeOnStatusChange = () => {
    guardians.forEach((g: any) => {
      const note = {
        notetype: "intake",
        subject: {
          id: g?.id,
          type: "guardian",
        },
        fields: {
          description: intakeNote,
          date: new Date().toISOString().split("T")[0],
        },
      };

      //save intake note
      postNote(note, id)
        .then((res: any) => {
          const noteId = res[0].data.note_id;

          //update user intake data
          const intakeData = {
            intake_at: intakeDate,
            intake_note_id: noteId,
            exit_at: null,
            exit_note_id: null,
          };

          patchGuardian(intakeData, id, g?.id).catch((err: any) => {
            console.log(err);
          });
        })
        .catch((err: any) => {
          setErrMsg(err?.response?.data?.msg || "Failed to save note");
        });
    });

    household.forEach((h: any) => {
      const note = {
        notetype: "intake",
        subject: {
          id: h?.id,
          type: "member",
        },
        fields: {
          description: intakeNote,
          date: new Date().toISOString().split("T")[0],
        },
      };

      //save intake note
      postNote(note, id)
        .then((res: any) => {
          const noteId = res[0].data.note_id;

          //update member intake data
          const intakeData = {
            intake_at: intakeDate,
            intake_note_id: noteId,
            exit_at: null,
            exit_note_id: null,
          };

          patchMember(intakeData, id, h.id).catch((err: any) => {
            console.log(err);
          });
        })
        .catch((err: any) => {
          setErrMsg(err?.response?.data?.msg || "Failed to save note");
        });
    });

    children.forEach((c: any) => {
      const note = {
        notetype: "intake",
        subject: {
          id: c?.id,
          type: "member",
        },
        fields: {
          description: intakeNote,
          date: new Date().toISOString().split("T")[0],
        },
      };

      //save intake note
      postNote(note, id)
        .then((res: any) => {
          const noteId = res[0].data.note_id;

          //update member intake data
          const intakeData = {
            intake_at: intakeDate,
            intake_note_id: noteId,
            exit_at: null,
            exit_note_id: null,
          };

          patchMember(intakeData, id, c.id).catch((err: any) => {
            console.log(err);
          });
        })
        .catch((err: any) => {
          setErrMsg(err?.response?.data?.msg || "Failed to save note");
        });
    });

    setShowExit(false);
  };

  const banOnStatusChange = async () => {
    guardians.map((g: any) => {
      const banObj = {
        is_banned: true,
        duration: duration,
        program: null,
        previous_job: g?.job,
        job: null,
      };

      banGuardian(banObj, id, g?.id).catch((err: any) => {
        console.log(err);
      });
    });

    household.map((h: any) => {
      performBanMember(h);
    });

    children.map((c: any) => {
      performBanMember(c);
    });
  };

  const performBanMember = (m: any) => {
    const banObj = {
      is_banned: true,
      duration: duration,
      program: null,
      previous_job: m?.job,
      job: null,
    };

    banMember(banObj, id, m?.id).catch((err: any) => {
      console.log(err);
    });
  };

  const updateName = (e: any) => {
    setErrMsg("");
    setName(e.target.value);
    if (e.target.value) {
      setSaveIsEnabled(true);
    } else {
      setSaveIsEnabled(false);
    }
  };

  const updatePrimaryCM = (e: any) => {
    if (e.target.value == secondaryCM && e.target.value !== "0") {
      setErrMsg("Primary Case Manager cannot be the same as Secondary Case Manager");
    } else {
      setPrimaryCM(e.target.value);
      enableSave();
    }
  };

  const updateSecondaryCM = (e: any) => {
    if (e.target.value == primaryCM && e.target.value !== "0") {
      setErrMsg("Secondary Case Manager cannot be the same as Primary Case Manager");
    } else {
      setSecondaryCM(e.target.value);
      enableSave();
    }
  };

  const updateCaseStatus = (e: any) => {
    setStatus(e.target.value);

    if (initialStatus === "active" && e.target.value !== "active") {
      setShowExit(true);
      setShowIntake(false);
    } else if (initialStatus !== "active" && e.target.value === "active") {
      setShowExit(false);
      setShowIntake(true);
    } else {
      setShowExit(false);
      setShowIntake(false);
    }

    enableSave();
  };

  const updateAddressOne = (e: any) => {
    setAddressOne(e.target.value);
    enableSave();
  };

  const updateAddressTwo = (e: any) => {
    setAddressTwo(e.target.value);
    enableSave();
  };

  const updateCity = (e: any) => {
    setCity(e.target.value);
    enableSave();
  };

  const updateState = (e: any) => {
    setState(e.target.value);
    enableSave();
  };

  const updateZip = (e: any) => {
    setZip(e.target.value);
    enableSave();
  };

  const onClickCreateFamily = () => {
    setShowCreateFamily(true);
  };

  function enableSave() {
    setErrMsg("");
    if (name) {
      setSaveIsEnabled(true);
    } else {
      setSaveIsEnabled(false);
    }
  }

  const onClickGoToCase = () => {
    if (saveIsEnabled) {
      setLeavePath(`/my-cases/${id}`);
      setShowModal(true);
    } else {
      navigate(`/my-cases/${id}`);
      setActivePage("/my-cases");
    }
  };

  const onClickDoneCreateUser = () => {
    if (userToSave) {
      setShowModal(true);
    } else {
      getHousehold();
      setShowCreateFamily(false);
      setUserToSave(false);
    }
  };

  const onClickLeaveCreateUserModal = () => {
    getHousehold();
    setShowCreateFamily(false);
    setUserToSave(false);
    setShowModal(false);
  };

  const onClickDoneEditUser = () => {
    if (userToSave) {
      setShowModal(true);
    } else {
      getHousehold();
      setShowEditFamily(false);
      setSelectedUser(undefined);
      setUserType("");
      setUserToSave(false);
    }
  };

  const onClickLeaveEditUserModal = () => {
    getHousehold();
    setShowEditFamily(false);
    setSelectedUser(undefined);
    setUserType("");
    setUserToSave(false);
    setShowModal(false);
  };

  return (
    <>
      <ToastMsg showToast={showToast} setShowToast={setShowToast} toastText={toastText} toastBody={toastBody} />

      {isLoading ? (
        <Container className={showMenu ? "lg div-main-menu" : "lg div-main"}>
          <Button size="sm" className="spinner-button spinner-button-main">
            <Spinner animation="border" style={{ color: "#F6893D" }} />
          </Button>
        </Container>
      ) : (
        <Container className={showMenu ? "lg div-main-menu pb-5" : "lg div-main pb-5"}>
          {showCreateFamily ? (
            <CreateMember
              caseStatus={status}
              setShowCreateFamily={setShowCreateFamily}
              startingGuardians={guardians}
              startingHousehold={household}
              startingChildren={children}
              setUserToSave={setUserToSave}
              onClickDoneCreate={onClickDoneCreateUser}
              getHousehold={getHousehold}
            />
          ) : showEditFamily ? (
            <EditMember
              user={selectedUser}
              userType={userType}
              setUserToSave={setUserToSave}
              caseStatus={status}
              onClickDone={onClickDoneEditUser}
              guardians={guardians}
              getHousehold={getHousehold}
            />
          ) : (
            <>
              <p className={errMsg ? "errmsg" : "offscreen"} aria-live="assertive">
                {errMsg}
              </p>
              <Row className="mb-3">
                <Col xs={6} className="text-start">
                  <Button className={"app-primary-color button"} onClick={(e) => backCase()}>
                    {"Back to Cases"}
                  </Button>
                </Col>
                <Col xs={6} className="text-end">
                  <Button className="app-primary-color me-3 button" onClick={(e) => onClickCreateFamily()}>
                    Add Member
                  </Button>
                  {!limitedAccess && (
                    <Button id="pagetitle-button" className={"app-primary-color button me-3"} onClick={onClickGoToCase}>
                      {"View Case"}
                    </Button>
                  )}
                  <Button id="pagetitle-button" className={"app-secondary-color button"} onClick={saveCase} disabled={!saveIsEnabled}>
                    Save
                  </Button>
                </Col>
              </Row>
              <PageTitle title={"Edit Case"} />
              <Row>
                <Col xs={12} lg={6} className="mb-3">
                  <h4 className="app-header-text mt-0">Details</h4>
                  <Stack gap={3}>
                    <Input
                      label="Name"
                      type="text"
                      value={name}
                      onUpdate={(e: any) => updateName(e)}
                      required={true}
                      showCharCount
                      maxLength={75}
                    />
                    <Row>
                      <Col xs={6}>
                        <SelectObject
                          label="Primary Case Manager"
                          value={primaryCM}
                          options={cmUsers}
                          onUpdate={(e: any) => updatePrimaryCM(e)}
                        />
                      </Col>
                      <Col xs={6}>
                        <SelectObject
                          label="Secondary Case Manager"
                          value={secondaryCM}
                          options={cmUsers}
                          onUpdate={(e: any) => updateSecondaryCM(e)}
                        />
                      </Col>
                    </Row>
                    <Row>
                      <Col xs={6}>
                        <SelectObject
                          label="Status"
                          value={status}
                          options={statuses}
                          onUpdate={(e: any) => updateCaseStatus(e)}
                          skipInitialOption={true}
                        />
                      </Col>
                    </Row>
                  </Stack>
                  {showExit && (
                    <Row className="mt-3">
                      <ExitNote
                        exitDate={exitDate}
                        setExitDate={setExitDate}
                        exitNote={exitNote}
                        setExitNote={setExitNote}
                        exitType={exitType}
                        setExitType={setExitType}
                        includeBan={true}
                        isBanned={isBanned}
                        setIsBanned={setIsBanned}
                        duration={duration}
                        setDuration={setDuration}
                      />
                    </Row>
                  )}
                  {showIntake && (
                    <Row className="mt-3">
                      <IntakeNote
                        intakeDate={intakeDate}
                        setIntakeDate={setIntakeDate}
                        intakeNote={intakeNote}
                        setIntakeNote={setIntakeNote}
                        focus={true}
                      />
                    </Row>
                  )}
                </Col>
                <Col xs={12} lg={6}>
                  <h4 className="app-header-text">Address</h4>
                  <Stack gap={3}>
                    <Input label="Address Line One" type="text" value={addressOne} onUpdate={(e: any) => updateAddressOne(e)} />
                    <Input label="Address Line Two" type="text" value={addressTwo} onUpdate={(e: any) => updateAddressTwo(e)} />
                    <Row>
                      <Col xs={4}>
                        <Input label="City" type="text" value={city} onUpdate={(e: any) => updateCity(e)} />
                      </Col>
                      <Col xs={4}>
                        <SelectObject label="State" value={state} options={statesList} onUpdate={(e: any) => updateState(e)} />
                      </Col>
                      <Col xs={4}>
                        <Input label="Zip" type="text" value={zip} onUpdate={(e: any) => updateZip(e)} />
                      </Col>
                    </Row>
                  </Stack>
                </Col>
              </Row>
              <>
                {(guardians.length > 0 || household.length > 0) && <h4 className="mt-3 app-header-text">Adults</h4>}
                <Row>
                  {guardians.map((user: any, index: number) => (
                    <HouseholdCard
                      key={user?.id}
                      user={user}
                      selectedUser={selectedUser}
                      setSelectedUser={setSelectedUser}
                      setUserType={setUserType}
                      setShowEditFamily={setShowEditFamily}
                    />
                  ))}
                  {household.map((user: any, index: number) => (
                    <HouseholdCard
                      key={user?.id}
                      user={user}
                      selectedUser={selectedUser}
                      setSelectedUser={setSelectedUser}
                      setUserType={setUserType}
                      setShowEditFamily={setShowEditFamily}
                    />
                  ))}
                </Row>
                {children.length > 0 && (
                  <>
                    <h4 className="mt-3 app-header-text">Children</h4>
                    <Row className="mb-2">
                      {children.map((user: any, index: number) => (
                        <HouseholdCard
                          key={user?.id}
                          user={user}
                          selectedUser={selectedUser}
                          setSelectedUser={setSelectedUser}
                          setUserType={setUserType}
                          setShowEditFamily={setShowEditFamily}
                        />
                      ))}
                    </Row>
                  </>
                )}
              </>
            </>
          )}

          {showEditFamily ? (
            <SaveChangesModal showModal={showModal} setShowModal={setShowModal} onLeave={onClickLeaveEditUserModal} />
          ) : showCreateFamily ? (
            <SaveChangesModal showModal={showModal} setShowModal={setShowModal} onLeave={onClickLeaveCreateUserModal} />
          ) : (
            <SaveChangesModal showModal={showModal} setShowModal={setShowModal} path={leavePath} />
          )}
        </Container>
      )}
    </>
  );
};

export default EditCase;
